import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AppointmentEventType, EmployeeAvailabilityType, EventsByResourceType, EventsListType, EventType, ResourceGroupType, SchedulerDraggableId, SchedulerDraggingPlaceholder, SchedulerDroppableId, SchedulingBoardType, SchedulingView, TimeBlock, TimeRowEventType } from './SchedulingTypes';
import { useGlobalContext } from 'GlobalContext';
import { useSettings } from 'services/settings/SettingsContext';
import { DragDropEvent, useDragDropContext } from 'services/dragdrop/DragDropContext';
import { handleDragJobInboxJobToResource } from './functions/handleDragJobInboxJobToResource';
import { handleAppointmentDrag } from './functions/handleAppointmentDrag';
import { handleAppointmentResize } from './functions/handleAppointmentResize';
import JobInbox from './JobInbox';
import { calculateDurationMinutesFromDecimal, determineEventFetchParams } from './functions/schedulingDateUtils';
import EmployeeSchedulerResources from './EmployeeSchedulerResources';
import { generateEvents } from './functions/generateEvents';
import { useFetchData } from 'services/api/useFetchData';
import { fetchData } from 'services/api/fetchData';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { handleActivityDrag } from './functions/handleActivityDrag';
import { usePackageFeature, useActiveFeature } from 'services/permissions/permissionChecks';
import { useSubcontractorDayPassCheck } from 'services/subcontractors/useSubcontractorDayPassCheck';
import SchedulerMap, { MapFocusAppointment } from './map/SchedulerMap';
import MapResizeHandle from './map/MapResizeHandle';
import MapPreviewCard from './map/MapPreviewCard';

// Minimum and maximum pixel width the side panel (job inbox / map) can be resized to
const MIN_SIDE_PANEL_WIDTH = 320;
const MAX_SIDE_PANEL_WIDTH_RATIO = 0.7;

// Default side panel width ratios, used until the planner manually resizes the panel
const DEFAULT_JOB_INBOX_WIDTH_RATIO = 0.25;
const DEFAULT_MAP_WIDTH_RATIO = 0.4;

interface EmployeeSchedulerProps {
    view: SchedulingView;
    schedulingBoard: SchedulingBoardType | null;
    resourceGroups: ResourceGroupType[];
    selectedResourceGroups: number[];
    days: Date[];
    hours: string[] | undefined;
    timeSlots: string[] | undefined;
    timeBlocks: TimeBlock[];
    dragResult: DragDropEvent<SchedulerDraggableId, SchedulerDroppableId> | null;
    refreshTrigger: number;
    setResourceGroups: React.Dispatch<React.SetStateAction<ResourceGroupType[]>>;
    setView: React.Dispatch<React.SetStateAction<SchedulingView | null>>;
    setLoading: (loading: string) => void;
    onRefresh: () => void;
}

const EmployeeScheduler: React.FC<EmployeeSchedulerProps> = ({
    view, schedulingBoard, resourceGroups, selectedResourceGroups, days, hours, timeSlots, timeBlocks, 
    dragResult, refreshTrigger, setResourceGroups, setView, setLoading, onRefresh
}) => {
    const { setFloatingAlert } = useGlobalContext();
    const { handleLogout } = useAuthContext();
    const { environmentSettings, userLocale, userTimezone } = useSettings();
    const { dragging, isDragging, dragOver } = useDragDropContext<SchedulerDraggableId, SchedulerDroppableId>();
    const { checkDayPasses, handleDayPassError } = useSubcontractorDayPassCheck();
    const [draggingPlaceholder, setDraggingPlaceholder] = useState<SchedulerDraggingPlaceholder | null>(null);
    const [events, setEvents] = useState<EventsListType>({});
    const [eventsByResource, setEventsByResource] = useState<EventsByResourceType>({});
    const [employeeAvailability, setEmployeeAvailability] = useState<EmployeeAvailabilityType>({});
    const [eventOverlapMap, setEventOverlapMap] = useState(new Map<string, EventType[]>());
    const [toScheduleListHeight, setToScheduleListHeight] = useState<number>(0);
    const [isInitialResizeDone, setIsInitialResizeDone] = useState(false);
    const [lastSync, setLastSync] = useState<string | null>(null);
    const [mapRefreshTick, setMapRefreshTick] = useState(0);
    const hasMapSchedulerPackageFeature = usePackageFeature('map_scheduler');
    const hasMapSchedulerActiveFeature = useActiveFeature('map_scheduler');
    const hasMapSchedulerFeature = hasMapSchedulerPackageFeature && hasMapSchedulerActiveFeature;
    const [sidePanelWidthPx, setSidePanelWidthPx] = useState<number | null>(null);
    const [mapFullscreen, setMapFullscreen] = useState(false);
    const [focusAppointment, setFocusAppointment] = useState<MapFocusAppointment | null>(null);
    
    // Update the event fetch params
    const eventFetchParams = useMemo(() => {
        if (!days || days.length === 0 || !schedulingBoard) return {};
        return determineEventFetchParams(days, schedulingBoard.id, selectedResourceGroups);
    }, [JSON.stringify(days), schedulingBoard?.id, selectedResourceGroups]);

    // Fetch the events
    const { response: fetchedEvents, loading: eventsLoading } = useFetchData({
        apiUrl: Object.keys(eventFetchParams).length > 0 ? "get_events" : "",
        params: eventFetchParams
    });

    // Update the loading state
    useEffect(() => {
        setLoading(eventsLoading);
    }, [eventsLoading]);

    // Set the fetched events
    useEffect(() => {
        if (fetchedEvents) {
            const { generatedEvents, eventsByResource, updatedOverlapMap } = generateEvents(
                fetchedEvents, userLocale, userTimezone, view?.timeDisplayMode === 'timeslots', days, resourceGroups, events, eventOverlapMap
            );
            setEvents(generatedEvents);
            setEventsByResource(eventsByResource);
            setEventOverlapMap(updatedOverlapMap);
            setEmployeeAvailability(fetchedEvents.employee_availability || {});

            // Set the last sync time to start the polling for updates
            setLastSync(fetchedEvents.server_time);
        }
    }, [fetchedEvents]);

    // Poll for updates  
    const fetchDeltaEvents = useCallback(async () => {
        if (Object.keys(eventFetchParams).length > 0 && lastSync) {

            // Get the last changed events 
            const response = await fetchData({
                apiUrl: 'get_events',
                params: { ...eventFetchParams, modified_since: lastSync },
                handleLogout
            });

            if (response) {
                // Regenerate the events
                const { generatedEvents, eventsByResource, updatedOverlapMap } = generateEvents(
                    response, userLocale, userTimezone, view?.timeDisplayMode === 'timeslots', days, resourceGroups, events, eventOverlapMap, true
                );

                // Update the event states
                setEvents(generatedEvents);
                setEventsByResource(eventsByResource);
                setEventOverlapMap(updatedOverlapMap);
                setEmployeeAvailability(response.employee_availability || {});

                // Update the last sync time
                setLastSync(response.server_time);

                // Notify the scheduler map so it refreshes on the same polling moment
                setMapRefreshTick(previous => previous + 1);
            }
        }
    }, [eventFetchParams, lastSync, handleLogout, userLocale, userTimezone, view?.timeDisplayMode, days, resourceGroups, events, eventOverlapMap]);

    // Refresh the events every 10 seconds
    useEffect(() => {
        const interval = setInterval(fetchDeltaEvents, 10000);
        return () => clearInterval(interval);
    }, [fetchDeltaEvents]);

    // Refresh the events on trigger, for example after adding a job
    useEffect(() => {
        fetchDeltaEvents();
    }, [refreshTrigger]);

    // Handle the completion of a job or event drag
    useEffect(() => {
        if (dragResult) {
            const handleDragResult = async () => {
                switch (dragResult.draggableId.type) {
                    // Handle drag from the job inbox to a resource daylist or timeslot
                    case 'jobInbox':
                        handleDragJobInboxJobToResource(
                            dragResult,
                            events,
                            eventsByResource,
                            resourceGroups,
                            userLocale,
                            userTimezone,
                            environmentSettings,
                            setFloatingAlert,
                            setEvents,
                            setEventsByResource,
                            employeeAvailability,
                            onRefresh,
                            checkDayPasses,
                            handleDayPassError
                        );
                        break;

                    // Handle appointment drag
                    case 'appointment':
                        handleAppointmentDrag(
                            dragResult, 
                            events,
                            resourceGroups,
                            userLocale, 
                            userTimezone,
                            setFloatingAlert, 
                            setEvents, 
                            setEventsByResource,
                            checkDayPasses,
                            handleDayPassError
                        );
                        break;

                    // Handle activity drag
                    case 'activity':
                        handleActivityDrag(
                            dragResult, 
                            events,
                            resourceGroups,
                            userLocale, 
                            userTimezone,
                            setFloatingAlert, 
                            setEvents, 
                            setEventsByResource
                        );

                    default:
                        return null;
                }
            }
            handleDragResult();
        }
    }, [dragResult]);

    // Set the dragging placeholder
    useEffect(() => {
        // Reset dragging placeholder
        if (!isDragging || !dragOver) {
            setDraggingPlaceholder(null);
            return;
        }
    
        // Determine the duration minutes
        let durationMinutes = 60;
        if (dragging?.draggableId?.type === 'appointment' || dragging?.draggableId?.type === 'activity') {
            durationMinutes = dragging.draggableId.event.durationMinutes ?? 60;
        }

        if (dragging?.draggableId?.type === 'jobInbox') {
            // Get the estimated duration from the job
            const jobEstimatedDuration = dragging.draggableId.job.estimated_duration;
            if (jobEstimatedDuration != null) {
                durationMinutes = calculateDurationMinutesFromDecimal(jobEstimatedDuration);

            // Fallback to 60 minutes if no estimated duration is set
            } else {
                durationMinutes = 60;
            }
        }
    
        // Set the dragging placeholder for dragging over a timeslot
        if (dragOver.type === 'timeSlot') {
            const { resourceId, day, timeSlot } = dragOver;
            setDraggingPlaceholder({ resourceId, day, timeSlot, durationMinutes });

        // Set the dragging placeholder for dragging over a day list
        } else if (dragOver.type === 'dayList') {
            const { resourceId, day } = dragOver;
            setDraggingPlaceholder({ resourceId, day, durationMinutes });
        }
    }, [isDragging, dragOver, dragging]);

    // Get the division ids from the selected resource groups
    const selectedDivisions = useMemo(() =>
        Array.from(new Set(resourceGroups
            .filter(resourceGroup => selectedResourceGroups.includes(resourceGroup.id as number))
            .map(resourceGroup => resourceGroup.division)
            .filter((division): division is number => division !== null)
        )), [resourceGroups, selectedResourceGroups]
    );

    // Switch the side panel to the map and focus the appointment's location, for the right click
    // "show on map" action on both appointment and timerow events
    const handleShowAppointmentOnMap = useCallback((event: AppointmentEventType | TimeRowEventType) => {
        setView(prev => prev && ({ ...prev, showJobInbox: true, showSchedulerMap: true }));
        setFocusAppointment({
            id: event.appointmentId ?? 0,
            latitude: event.job.work_location_latitude ?? null,
            longitude: event.job.work_location_longitude ?? null,
        });
    }, [setView]);

    // Adjust the side panel width while the resize handle is being dragged
    const handleSidePanelResize = useCallback((deltaX: number) => {
        setSidePanelWidthPx(prev => {
            const currentWidth = prev ?? window.innerWidth * (view.showSchedulerMap ? DEFAULT_MAP_WIDTH_RATIO : DEFAULT_JOB_INBOX_WIDTH_RATIO);
            const maxWidth = window.innerWidth * MAX_SIDE_PANEL_WIDTH_RATIO;
            return Math.min(Math.max(currentWidth - deltaX, MIN_SIDE_PANEL_WIDTH), maxWidth);
        });
    }, [view.showSchedulerMap]);

    // Trigger a window resize after panel width or fullscreen changes are applied to the dom,
    // so the map canvas and the calendar grid recalculate to their new dimensions
    useEffect(() => {
        window.dispatchEvent(new Event('resize'));
    }, [sidePanelWidthPx, mapFullscreen, view.showSchedulerMap]);

    // The side panel is shown based on the existing job inbox visibility setting - the map
    // (when the feature is active) lives inside that same panel, replacing its content
    const showSidePanel = view.showJobInbox;
    const showMap = hasMapSchedulerFeature && view.showSchedulerMap;

    // The days of the current scheduler period the map can display
    const mapPeriodDays = useMemo(() => (
        days.length > 0 ? days : [new Date()]
    ), [JSON.stringify(days)]);

    // Default the panel to a wider ratio when showing the map than when showing the job inbox,
    // unless the planner has already manually resized it via the drag handle
    const defaultSidePanelRatio = showMap ? DEFAULT_MAP_WIDTH_RATIO : DEFAULT_JOB_INBOX_WIDTH_RATIO;
    const effectiveSidePanelWidthPx = showSidePanel ? (sidePanelWidthPx ?? window.innerWidth * defaultSidePanelRatio) : null;

    return (
        <div
            className={`scheduling-board ${view.direction} ${view.viewType} grid-${days.length}-columns ${showSidePanel ? 'show-inbox' : ''}`}
            style={effectiveSidePanelWidthPx ? { gridTemplateColumns: `auto ${effectiveSidePanelWidthPx}px` } : undefined}
        >

            {/* Render resources */}
            <div style={{ visibility: isInitialResizeDone ? 'visible' : 'hidden' }}>
                <EmployeeSchedulerResources
                    view={view}
                    resourceGroups={resourceGroups}
                    days={days}
                    hours={hours}
                    timeSlots={timeSlots}
                    isDragging={isDragging}
                    draggingPlaceholder={draggingPlaceholder}
                    timeBlocks={timeBlocks}
                    events={events}
                    eventsByResource={eventsByResource}
                    eventOverlapMap={eventOverlapMap}
                    employeeAvailability={employeeAvailability}
                    setResourceGroups={setResourceGroups}
                    onScheduleListHeightChange={(height) => setToScheduleListHeight(height)}
                    onInitialResizeDone={(isDone) => setIsInitialResizeDone(isDone)}
                    onViewChange={setView}
                    onRefresh={onRefresh}
                    onShowAppointmentOnMap={hasMapSchedulerFeature ? handleShowAppointmentOnMap : undefined}
                    onAppointmentResize={(resizedEvent, newDurationMinutes) => handleAppointmentResize(resizedEvent, newDurationMinutes, userLocale, userTimezone, setEvents)}
                    sidePanelWidthPx={effectiveSidePanelWidthPx}
                    showSidePanel={showSidePanel}
                />
            </div>

            {/* Render job inbox / scheduler map side panel */}
            {showSidePanel && (
                <div className="scheduling-side-panel">
                    <MapResizeHandle onResize={handleSidePanelResize} />

                    {showMap ? (
                        <SchedulerMap
                            selectedDivisions={selectedDivisions}
                            periodDays={mapPeriodDays}
                            period={view.period}
                            refreshTick={mapRefreshTick}
                            focusAppointment={focusAppointment}
                            fullscreen={mapFullscreen}
                            onToggleFullscreen={() => setMapFullscreen(prev => !prev)}
                        />
                    ) : (
                        <>
                            <JobInbox
                                selectedDivisions={selectedDivisions}
                                inboxHeight={toScheduleListHeight}
                                dragging={dragging}
                                isDragging={isDragging}
                                dragOver={dragOver}
                                dragResult={dragResult}
                                onLoaded={() => {}}
                            />
                            {hasMapSchedulerFeature && (
                                <MapPreviewCard
                                    onClick={() => setView(prev => prev && ({ ...prev, showSchedulerMap: true }))}
                                />
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    )
}

export default EmployeeScheduler;